<template>
  <VRow class="EventChart">
    <VCol
      cols="12"
      v-bind="chartColBind"
    >
      <VSwitch
        v-if="grouping === null && (chartCategories || []).length"
        v-model="ui.chart.grouping"
        label="Детальный вид"
        hide-details
        dense
      />
      <VSwitch
        v-if="typesAsCategories === null && (chartCategories || []).length"
        v-model="ui.chart.typesAsCategories"
        label="Группировать по типу событий"
        hide-details
        dense
      />
      <!--VCard
        class="chart-helper-block"
        :style="{ display: ui.showChartHelper ? null : 'none' }"
      >
        <VCardTitle>
          Приближайте график мышкой
        </VCardTitle>
        <VCardText>
          <VImg
            alt="App logo"
            :src="require('../assets/chart-scale.gif')"
            max-height="100px"
          />
        </VCardText>
      </VCard-->
      <VSkeletonLoader
        v-if="loading"
        type="image"
      />
      <VueHighcharts
        v-else-if="(quickFilteredEvents || []).length"
        ref="chart"
        class="chart"
        :class="{ 'hide-zoom-out': hideZoomOut }"
        :options="chartOptions"
        :style="{ height: chartHeight + 'px' }"
        @mouseenter.native="ui.showChartHelper = true"
        @mouseleave.native="ui.showChartHelper = false"
      />
      <VAlert
        v-else-if="hasFilters"
        border="left"
        outlined
        color="primary"
        icon="mdi-calendar-remove"
      >
        Под выбранные фильтры нет подходящих событий, попробуйте
        <VBtn
          text
          small
          color="secondary"
          @click="resetFilters()"
        >
          сбросить фильтры
        </VBtn>
      </VAlert>
      <VAlert
        v-else-if="filtersVoidResult"
        border="left"
        outlined
        color="primary"
        icon="mdi-calendar-remove"
      >
        Пожалуйста выберите
        {{ showUserQuickFilter ? 'пользователя' : '' }}
        {{ (showUserQuickFilter && showEventTypeQuickFilter) ? 'и' : '' }}
        {{ (showEventTypeQuickFilter) ? 'тип события' : '' }}
      </VAlert>
      <VAlert
        v-else
        border="left"
        outlined
        color="primary"
        icon="mdi-calendar-remove"
      >
        На выбранную дату нет&nbsp;событий, как&nbsp;только они появятся &ndash;
        вы увидете их на&nbsp;диаграмме Гантта.
        <VBtn
          text
          small
          color="secondary"
          @click="$emit('reset-date')"
        >
          Выбрать другую дату
        </VBtn>
      </VAlert>
    </VCol>
    <VCol
      v-if="showEventTypeQuickFilter"
      cols="12"
      v-bind="quickFilterColBind"
    >
      <VSwitch
        v-for="type in eventTypesForQuickFilter"
        :key="type.code"
        :value="type.code"
        :input-value="ui.filters.eventTypeCodes"
        :label="type.name"
        inset
        hide-details
        @change="ui.filters.eventTypeCodes = [...$event]"
      />
    </VCol>
    <VCol
      v-if="showUserQuickFilter"
      cols="12"
      v-bind="quickFilterColBind"
    >
      <VSwitch
        v-for="userId in userIdsForQuickFilter"
        :key="userId"
        :value="userId"
        :input-value="ui.filters.userIds"
        :label="$store.getters['user/displayName'](userId)"
        inset
        hide-details
        @change="ui.filters.userIds = [...$event]"
      />
    </VCol>
  </VRow>
</template>

<script>
import Highcharts from 'highcharts'
import { Chart as VueHighcharts } from 'highcharts-vue'
import initXrange from 'highcharts/modules/xrange'
import initExporting from 'highcharts/modules/exporting'
import moment from 'moment'
import Mustache from 'mustache'
import * as R from 'ramda'

import { capTitle } from '../helpers'

moment.locale('ru')

if (process.env.NODE_ENV === 'development') {
  window.moment = moment
  window.R = R
}

initExporting(Highcharts)
initXrange(Highcharts)
Highcharts.setOptions({
  exporting: {
    enabled: true,
    buttons: {
      contextButton: {
        menuItems: [
          'viewFullscreen',
          'printChart',
        ],
      },
    },
  },
  lang: {
    accessibility: {
      exporting: {
        chartMenuLabel: 'Меню чарта',
        exportRegionLabel: 'Меню чарта',
        menuButtonLabel: 'Смотреть меню чарта',
      },
    },
    contextButtonTitle: 'Контекстное меню чарта',
    decimalPoint: ',',
    downloadCSV: 'Скачать CSV',
    downloadJPEG: 'Скачать JPEG',
    downloadPDF: 'Скачать PDF',
    downloadPNG: 'Скачать PNG',
    downloadSVG: 'Скачать SVG',
    downloadXLS: 'Скачать XLS',
    drillUpText: '◁ Назад к {series.name}',
    loading: 'Загрузка...',
    months: moment.months(),
    noData: 'Нет данных для отображения',
    openInCloud: 'Открыть в облаке Highcharts',
    printButtonTitle: 'Печать',
    printChart: 'Печать чарта',
    rangeSelectorFrom: 'От',
    rangeSelectorTo: 'До',
    rangeSelectorZoom: 'Период',
    resetZoom: 'Отменить увеличение',
    resetZoomTitle: 'Сбросить увеличение (1:1)',
    shortMonths: moment.monthsShort(),
    thousandsSep: "'",
    viewData: 'Показать таблицу с данными',
    viewFullscreen: 'На весь экран',
    weekdays: moment.weekdays().map(capTitle),
  },
})

const defaultQuickFilters = () => ({
  userIds: [],
  eventTypeCodes: [],
})

const chartSettings = component => Object.freeze({
  // Минимальная ширина события на чарте, в долях, e.g.: 0.05 => 5%
  POINT_MIN_WIDTH: 0.005,
  CHART_OPTIONS: Object.freeze({
    chart: {
      type: 'xrange',
      styleMode: true,
      zoomType: 'x',
      events: {
        selection: ((self) => function(selectionEvent) {
          return self.onSelection(selectionEvent, this)
        })(component),
        render: ((self) => function(renderEvent) {
          return self.onRender(renderEvent, this)
        })(component),
      },
    },
    time: { timezoneOffset: (new Date()).getTimezoneOffset() },
    credits: { enabled: false },
    title: {
      text: 'События',
      style: { fontSize: '12px' },
    },
    xAxis: { type: 'datetime' },
    yAxis: {
      title: { text: '' },
      categories: [],
      reversed: false,
    },
    series: [],
    accessibility: {
      point: {
        descriptionFormatter: component.pointDescriptionFormatter,
      },
    },
    exporting: {
      sourceWidth: 1200,
    },
    plotOptions: {
      series: {
        // allowPointSelect: true,
        animation: { duration: 500 },
        // colorByPoint: true,
        states: {
          select: {
            animation: { duration: 300 },
          },
          hover: {
            animation: { duration: 300 },
          },
        },
        label: { enabled: false },
        // dataLabels: {
        //   enabled: true,
        //   align: 'left',
        //   format: '{point.series.name}',
        //   style: { fontWeight: 'normal', textOutline: '1px #fff', color: '#000' },
        // },
        cursor: 'pointer',
        events: {
          click: ((self) => function(clickEvent) {
            return self.onPointClick(clickEvent, this)
          })(component),
        },
      },
    },
  }),
  GANTT_TOOLTIP_TEMPLATE: `
    <b>{{{ userName }}}</b>: <i>{{{ event.type.name }}}</i><br />
    <b>Время:</b> {{ datetime }}<br />
    {{# showDuration }}
      <b>Продолжительность:</b> {{ duration }}<br />
    {{/ showDuration }}
    {{# description }}
      <b>Комментарий:</b> {{{ description }}}<br />
    {{/ description }}
  `.trim(),
})

export default {
  name: 'EventChart',
  components: {
    VueHighcharts,
  },
  props: {
    // events - from vuex getters['event/events']
    // Отобразить быстрый (client side) фильтр по пользователю
    userQuickFilter: { type: Boolean, default: false },
    // Отобразить быстрый (client side) фильтр по типу события
    eventTypeQuickFilter: { type: Boolean, default: false },
    // Заголовок в шапке чарта
    title: { type: String, default: 'События' },
    // Опционально можно передать список событий для вывода
    inputEvents: { type: Array, default: null },
    // Настройка группировки извне
    grouping: { type: Boolean, default: null },
    // Настройка группировки извне
    typesAsCategories: { type: Boolean, default: null },
    // Установить масштаб извне, timestamps
    forceXAxisMin: { type: Number, default: null },
    forceXAxisMax: { type: Number, default: null },
    // Скрыть кнопку сброса зума с чарта
    hideZoomOut: { type: Boolean, default: false },
  },
  data() {
    return {
      chart: chartSettings(this),
      ui: {
        filters: defaultQuickFilters(),
        // timestamp, минимальное время на текущем чарте с учетом зума
        chartMinXAxisTs: null,
        // timestamp, максимальное время на текущем чарте с учетом зума
        chartMaxXAxisTs: null,
        // Показ картинки с ресайзом при наведении на график
        // fixme: выключаем, т.к. бесит
        showChartHelper: false,
        chart: {
          // true - события разных типов на отдельных строчках
          // false - события разных типов на одном бегунке,
          grouping: false,
          typesAsCategories: false,
          legendHeight: 29,
        },
        zoomedIn: false,
      },
      momentCreated: moment().toISOString(),
    }
  },
  computed: {
    authorized() { return !!this.$store.state.auth.token },
    eventTypeMap() { return this.$store.state.eventType.map },
    defaultQuickFilters() { // быстрые фильтры по-умолчанию
      return defaultQuickFilters()
      // const { userQuickFilter } = this
      // const user = this.$store.state.user.current
      // const filters = defaultQuickFilters()
      // if (userQuickFilter && user !== null) {
      //   filters.userIds.push(user.id)
      // }
      // return filters
    },
    loading() { return this.$store.state.event.loading }, // флаг загрузки событий
    events() { // События для чарта
      const { $store, inputEvents } = this
      return inputEvents === null
        ? $store.getters['event/events']
        : inputEvents
    },
    eventsTotal() { return this.$store.state.event.total }, // N событий на чарте
    earliestXAxisDt() {
      // Для подсчета масштаба событий на чарте, самая ранняя дата
      const { ui: { chartMinXAxisTs }, events } = this
      if (chartMinXAxisTs) {
        return moment(chartMinXAxisTs).toISOString()
      }
      if (events && events.length) {
        return moment(events[0].started).startOf('day').toISOString()
      }
      return null
    },
    latestXAxisDt() {
      // Для подсчета масштаба событий на чарте, самая поздняя дата
      const {
        momentCreated,
        chart: { POINT_MIN_WIDTH },
        earliestXAxisDt,
        ui: { chartMaxXAxisTs },
        events,
      } = this

      if (chartMaxXAxisTs) { // Уже поставлен зум, берем его
        return moment(chartMaxXAxisTs).toISOString()
      }

      if (events && events.length) { // Загружены события
        const finishedDts = R.pipe(
          R.map(e => {
            let finished = e.finished
            if (!finished && e.type.expand) {
              // Время завершения растягивается со сегодня/конца дня
              const start = moment(e.started)
              const todayStart = moment(momentCreated).startOf('day')
              const todayEnd = moment(momentCreated).endOf('day')
              finished =
                start.isBefore(todayStart)
                  ? start.endOf('day').toISOString()
                  : start.isAfter(todayEnd)
                    ? null
                    : moment(momentCreated).toISOString()
            }
            if (!finished) { // Время завершения не указано
              // Берем время начала
              const startedMoment = moment(e.started)
              const startedTs = startedMoment.valueOf()
              // И самое ранне время на чарте
              const earliestTs = moment(earliestXAxisDt)
              // Вычисляем минимальную продолжительность события для чарта
              const shortestEventDur =
                (startedTs - earliestTs) * POINT_MIN_WIDTH
              // Отображаемое время завершения: начало + мин. длительность
              finished =
                startedMoment.clone().add(shortestEventDur).toISOString()
            }
            return finished
          }),
          // Даты в ISO можно сортировать как строки
          R.sort(R.descend(R.identity)), // Простая сортировка строк по убыванию
        )(events)
        return moment(finishedDts[0]).endOf('day').toISOString()
      }
      return null
    },
    minEventDuration() {
      // Минимальная продолжительность события,
      // чтобы можно было разглядеть на чарте
      const {
        chart: { POINT_MIN_WIDTH },
        earliestXAxisDt, latestXAxisDt,
      } = this
      const minDuration = earliestXAxisDt && latestXAxisDt
        // (конец шкалы времени - начало шкалы времени) * мин. ширина в долях
        ? (moment(latestXAxisDt) - moment(earliestXAxisDt)) * POINT_MIN_WIDTH
        // 1 минута по умолч.
        : 60 * 1000
      // Если одно короткое событие, то 1% от длительности может выйти
      // слишком маленьким, ограничиваем - не менее 5 сек.
      return Math.max(5 * 1000, minDuration)
    },
    normalizedEvents() {
      // Проставляем время завершения так, чтобы гантт не ломался
      const { momentCreated, minEventDuration, events } = this
      // console.log('normalizedEvents', minEventDuration, events && events.length)
      return events && events.map(e => {
        const normalizedEvent = R.clone(e)
        // Копируем дату завершения
        normalizedEvent.finishedForChart = e.finished
        if (!normalizedEvent.finishedForChart && e.type.expand) {
          // Растягиваем растяжимые события
          const start = moment(e.started)
          const todayStart = moment(momentCreated).startOf('day')
          const todayEnd = moment(momentCreated).endOf('day')
          normalizedEvent.finishedForChart =
            start.isBefore(todayStart)
              ? start.endOf('day').toISOString()
              : start.isAfter(todayEnd)
                ? null
                : moment(momentCreated).toISOString()
        }
        if (
          !normalizedEvent.finishedForChart || (
            moment(normalizedEvent.finishedForChart)
              .diff(moment(e.started)) < minEventDuration
          )
        ) {
          // Устанавливаем фиктивную дату для очень кратких моментов,
          // чтобы можно было разглядеть на графике
          normalizedEvent.finishedForChart =
            moment(e.started).add(minEventDuration).toISOString()
        }
        // Если дата завершения проставлена и
        // НЕ опережает начало хотя бы на 1 сек. -
        // устанавливаем длительность события - 1 секунда
        if (
          e.finished && (
            moment(e.finished).diff(moment(e.started)) < 1000
          )
        ) {
          // Устанавливаем фиктивную дату для очень кратких моментов,
          // чтобы можно было разглядеть на графике
          normalizedEvent.finished =
            moment(e.started).add(1001).toISOString()
        } else {
          normalizedEvent.finished = null
        }
        return normalizedEvent
      })
    },
    userIdsForQuickFilter() {
      const { normalizedEvents: events } = this
      return events && R.pipe(
        R.map(R.prop('userId')),
        R.uniq,
        R.sortBy(R.identity),
      )(events)
    },
    showUserQuickFilter() {
      const {
        userQuickFilter,
        loading,
        normalizedEvents: events,
        userIdsForQuickFilter,
      } = this
      return Boolean(
        !!userQuickFilter && !loading && events && events.length &&
        (userIdsForQuickFilter || []).length > 1)
    },
    eventTypesForQuickFilter() {
      const { normalizedEvents: events } = this
      return events && R.pipe(
        R.map(R.prop('type')),
        R.uniqBy(R.prop('code')),
        R.sortBy(R.prop('code')),
      )(events)
    },
    showEventTypeQuickFilter() {
      const {
        eventTypeQuickFilter,
        loading,
        normalizedEvents: events,
        eventTypesForQuickFilter,
      } = this
      return Boolean(
        eventTypeQuickFilter && !loading && events && events.length &&
        (eventTypesForQuickFilter || []).length > 1)
    },
    chartColBind() { // <v-col cols="12" :md="??" :lg="??">...<v-col>
      const xor = (a, b) => Boolean(a ^ b) // either but not both
      const { showUserQuickFilter, showEventTypeQuickFilter } = this

      if (xor(showUserQuickFilter, showEventTypeQuickFilter)) {
        // exactly 1 quick filter column on the right
        return { sm: '8', md: '9', lg: '10' }
      }
      return { sm: '12', md: '12', lg: '12' } // 12 cols
    },
    quickFilterColBind() { // <v-col cols="12" :md="??" :lg="??">...<v-col>
      const xor = (a, b) => Boolean(a ^ b) // either but not both
      const { showUserQuickFilter, showEventTypeQuickFilter } = this

      if (xor(showUserQuickFilter, showEventTypeQuickFilter)) {
        // exactly 1 quick filter column
        return { sm: '4', md: '3', lg: '2' }
      }
      return { sm: '6', md: '6', lg: '6' } // 6 cols
    },
    quickFilteredEvents() {
      const {
        normalizedEvents: events, ui: { filters },
      } = this
      if (!events) return null
      let filteredEvents = events

      if (filters.eventTypeCodes.length) {
        filteredEvents = filteredEvents
          .filter(e => filters.eventTypeCodes.includes(e.type.code))
      }

      if (filters.userIds.length) {
        filteredEvents = filteredEvents
          .filter(e => filters.userIds.includes(e.userId))
      }

      return filteredEvents
    },
    userIdsOnChart() {
      const { quickFilteredEvents: events } = this
      return events && R.pipe(
        R.map(R.prop('userId')),
        R.uniq,
        R.sortBy(R.identity),
      )(events)
    },
    eventsCountPerUser() {
      const { quickFilteredEvents: events, userIdsOnChart } = this
      return userIdsOnChart && events && userIdsOnChart.reduce(
        (map, userId) => {
          map[userId] =
            events.filter(e => e.userId === userId).length
          return map
        }, {})
    },
    eventTypesOnChart() {
      const { $store, quickFilteredEvents: events } = this
      return events && $store.state.category.map && R.pipe(
        R.map(R.path(['type'])),
        R.uniqBy(R.prop('code')),
        R.sortWith([
          R.descend(event => $store.state.category.map[event.categoryCode].name),
          R.descend(R.prop('sort')),
          R.descend(R.prop('name')),
        ]),
      )(events)
    },
    eventsCountPerType() {
      const { quickFilteredEvents: events, eventTypesOnChart } = this
      return eventTypesOnChart && events && eventTypesOnChart.reduce(
        (map, eventType) => {
          map[eventType.code] =
            events.filter(e => e.typeCode === eventType.code).length
          return map
        }, {})
    },
    chartCategories() {
      const {
        $store,
        userIdsOnChart: userIds,
        eventTypesOnChart,
        eventsCountPerType,
        eventsCountPerUser,
        ui: { chart: { typesAsCategories } },
      } = this
      return typesAsCategories
        ? eventTypesOnChart && eventTypesOnChart.map(type =>
          `<b>${$store.state.category.map[type.categoryCode].name}</b>: ` +
          `${type.name} <i>(${eventsCountPerType[type.code]})</i>`)
        : userIds && userIds.map(userId =>
          `${$store.getters['user/displayName'](userId)} ` +
          `<i>(${eventsCountPerUser[userId]})</i>`)
    },
    userIdToIndexOnChart() {
      const { userIdsOnChart: userIds } = this
      return userIds && Object.fromEntries(userIds.map((id, i) => [id, i]))
    },
    eventTypeCodeToIndexOnChart() {
      const { eventTypesOnChart } = this
      return eventTypesOnChart &&
        Object.fromEntries(eventTypesOnChart.map((type, i) => [type.code, i]))
    },
    chartSeries() {
      const {
        // $store,
        quickFilteredEvents: events,
        eventTypeMap,
        userIdToIndexOnChart,
        eventTypeCodeToIndexOnChart,
        ui: { chart: { typesAsCategories } },
      } = this
      return typesAsCategories
        // ? events && R.pipe(
        //   // Группируем события по пользователю
        //   R.groupBy(R.prop('userId')),
        //   // Разбиваем на пару [42, [... list of events ...]]
        //   R.toPairs,
        //   // Сортируем пользователей по алфавиту
        //   R.sortBy(pair => $store.getters['user/displayName'](pair[0])),
        //   R.map(([ userId, events ]) => ({
        //     name: $store.getters['user/displayName'](userId),
        //     // borderColor: '#00ff00',
        //     borderColor: '',
        //     pointWidth: 15,
        //     tooltip: {
        //       headerFormat: '',
        //       pointFormatter: (self => function() {
        //         return self.pointFormatter(this)
        //       })(this),
        //       followPointer: true,
        //     },
        //     data: events.map(event => ({
        //       x: moment(event.started).valueOf(),
        //       x2: moment(event.finishedForChart).valueOf(),
        //       y: eventTypeCodeToIndexOnChart[event.typeCode],
        //       color: event.type.color,
        //       _eventId: event.id,
        //     })),
        //     color: events.length ? events[0].type.color : null,
        //     dataLabels: {
        //       enabled: true,
        //     },
        //     colorByPoint: false,
        //   })),
        // )(events)
        ? events && R.pipe(
          // Группируем события по типу
          R.groupBy(R.path(['type', 'code'])),
          // Разбиваем на пару ['type-code', [... list of events ...]]
          R.toPairs,
          // Сортируем по сортировке типа
          R.sortBy(pair => eventTypeMap[pair[0]].sort),
          R.map(([ typeCode, events ]) => ({
            name: eventTypeMap[typeCode].name,
            // borderColor: '#00ff00',
            borderColor: '',
            pointWidth: 15,
            tooltip: {
              headerFormat: '',
              pointFormatter: (self => function() {
                return self.pointFormatter(this)
              })(this),
              followPointer: true,
            },
            data: events.map(event => ({
              x: moment(event.started).valueOf(),
              x2: moment(event.finishedForChart).valueOf(),
              y: eventTypeCodeToIndexOnChart[event.typeCode],
              color: event.type.color,
              _eventId: event.id,
            })),
            color: events.length ? events[0].type.color : null,
            dataLabels: {
              enabled: true,
            },
            colorByPoint: false,
          })),
        )(events)
        : events && R.pipe(
          // Группируем события по типу
          R.groupBy(R.path(['type', 'code'])),
          // Разбиваем на пару ['type-code', [... list of events ...]]
          R.toPairs,
          // Сортируем по сортировке типа
          R.sortBy(pair => eventTypeMap[pair[0]].sort),
          R.map(([ typeCode, events ]) => ({
            name: eventTypeMap[typeCode].name,
            // borderColor: '#00ff00',
            borderColor: '',
            pointWidth: 15,
            tooltip: {
              headerFormat: '',
              pointFormatter: (self => function() {
                return self.pointFormatter(this)
              })(this),
              followPointer: true,
            },
            data: events.map(event => ({
              x: moment(event.started).valueOf(),
              x2: moment(event.finishedForChart).valueOf(),
              y: userIdToIndexOnChart[event.userId],
              color: event.type.color,
              _eventId: event.id,
            })),
            color: events.map(e => e.type.color)[0],
            dataLabels: {
              enabled: true,
            },
            colorByPoint: false,
          })),
        )(events)
    },
    chartOptions() {
      const {
        events,
        chart: { CHART_OPTIONS },
        ui: { chart: { grouping } },
        chartSeries: series,
        chartCategories: categories,
        earliestXAxisDt,
        latestXAxisDt,
        forceXAxisMin,
        forceXAxisMax,
        title,
      } = this
      if (!events) return null
      return R.mergeDeepRight(
        CHART_OPTIONS,
        {
          series,
          yAxis: { categories, title: null },
          // yAxis: { categories, title: { text: title } },
          xAxis: {
            min: forceXAxisMin || moment(earliestXAxisDt).valueOf(),
            max: forceXAxisMax || moment(latestXAxisDt).valueOf(),
          },
          // Когда мы добавляем заголовок, кнопка экспорта не налезает на полоски
          title: { text: title },
          // title: null,
          plotOptions: {
            series: { grouping },
          },
        },
      )
    },
    chartHeight() {
      const {
        chartCategories: categories,
        eventTypesForQuickFilter: types,
        ui: { chart: { grouping, typesAsCategories, legendHeight } },
      } = this
      const TITLE_HEIGHT = 60
      const FOOTER_HEIGHT = 48 + legendHeight
      const typesN = Math.max(1, (types || []).length)
      // Высота полоски события: 15px, 6px запаса
      const CATEGORY_HEIGHT = (grouping && !typesAsCategories)
        ? 30 + typesN * 19
        : 19
      const categoriesN = Math.max(1, (categories || []).length)
      return TITLE_HEIGHT + (CATEGORY_HEIGHT * categoriesN) + FOOTER_HEIGHT
    },
    hasFilters() { // пользователь поменял что-то в фильтрах
      return !R.equals(this.ui.filters, this.defaultQuickFilters)
    },
    filtersVoidResult() { // выбранная конфигурация фильтров всегда возвращает пустой
      const {
        showEventTypeQuickFilter,
        showUserQuickFilter,
        ui: {
          filters: { userIds, eventTypeCodes },
        },
      } = this
      return (
        (showUserQuickFilter && !userIds.length) ||
        (showEventTypeQuickFilter && !eventTypeCodes.length))
    },
  },
  watch: {
    // reset quick filters on new events from server
    '$store.state.event.serverOrder': 'resetQuickFilters',
    defaultQuickFilters: {
      handler: 'resetQuickFilters',
      immediate: true,
    },
    // reflow chart on chart container height changed
    chartHeight: {
      handler: 'reflowChart',
      immediate: false,
    },
    chartCategories: {
      handler: 'reflowChart',
      immediate: false,
    },
    authorized: {
      handler(authorized) {
        // console.warn(`authorized inner watcher: ${authorized}`)
        if (authorized) {
          this.resetQuickFilters()
          this.getUsers()
          this.$store.dispatch('category/get')
        }
      },
      immediate: true,
    },
    grouping: {
      handler(grouping) {
        if (grouping !== null) {
          this.ui.chart.grouping = grouping
        }
      },
      immediate: true,
    },
    typesAsCategories: {
      handler(typesAsCategories) {
        if (typesAsCategories !== null) {
          this.ui.chart.typesAsCategories = typesAsCategories
        }
      },
      immediate: true,
    },
    forceXAxisMin() {
      if (this.$refs.chart) {
        this.$nextTick(() => {
          this.ui.chartMinXAxisTs =
            this.$refs.chart.chart.xAxis[0].getExtremes().min
        })
      }
    },
    forceXAxisMax() {
      if (this.$refs.chart) {
        this.$nextTick(() => {
          this.ui.chartMaxXAxisTs =
            this.$refs.chart.chart.xAxis[0].getExtremes().max
        })
      }
    },
    events() {
      // При обновлении списка событий, сбрасываем запомненные
      // таймштампы-экстремумы оси X, т.к. график будет перерисован
      this.ui.chartMinXAxisTs = null
      this.ui.chartMaxXAxisTs = null
    },
  },
  mounted() {
    const reflowSync = () => this.reflowChart(false)
    window.addEventListener('resize', reflowSync, false)
    this.$once('hook:beforeDestroy', () =>
      window.removeEventListener('resize', reflowSync, false))

    const unwatch = this.$watch(
      () => [this.forceXAxisMin, this.forceXAxisMax],
      ([newMin, newMax], [oldMin, oldMax]) => {
        if ((!newMax && oldMax) || (!newMin && oldMin)) {
          this.$refs.chart.chart.zoomOut()
        }
      }
    )
    this.$once('hook:beforeDestroy', unwatch)
  },
  methods: {
    resetQuickFilters() {
      const { ui, defaultQuickFilters } = this
      ui.filters = R.clone(defaultQuickFilters)
    },
    resetFilters() {
      // 1. Сбрасываем быстрые client-side фильтры
      this.resetQuickFilters()
      // 2. Пробрасываем событие, чтобы родитель мог отменить server-side фильтр
      this.$emit('reset-filters')
    },
    reflowChart(nextTick = true) {
      const reflow = () => {
        if (!this.$refs.chart) return
        this.$refs.chart.chart.reflow()
        setTimeout(() => {
          const legendPath = this.$el.querySelector('.highcharts-legend')
          if (!legendPath) return
          const legendHeight = legendPath.getBoundingClientRect().height
          if (this.ui.chart.legendHeight !== legendHeight) {
            this.ui.chart.legendHeight = legendHeight
          }
        }, 100)
      }
      if (nextTick) this.$nextTick(reflow)
      else reflow()
    },
    getUsers() { return this.$store.dispatch('user/getList') },
    ganttTooltip(event) {
      const { GANTT_TOOLTIP_TEMPLATE: template } = this.chart
      const start = event.started && moment(event.started)
      const end = event.finished && moment(event.finished)
      const duration = event.finished
        ? this.durationAsString(moment(event.started), moment(event.finished))
        : 'не завершено'
      const showDuration = !event.finished ||
        Math.abs(moment(event.started).diff(event.finished)) >= 1000
      return Mustache.render(template, {
        event,
        datetime: this.datetimeRangeAsString(start, end),
        duration,
        showDuration,
        description: event.description,
        userName: this.$store.getters['user/displayName'](event.userId),
      })
    },
    durationAsString(startMoment, endMoment) {
      const dur = moment.duration(endMoment.diff(startMoment))
      return [
        String(Math.floor(dur.asHours())).padStart(2, '0'),
        String(dur.minutes()).padStart(2, '0'),
        String(dur.seconds()).padStart(2, '0'),
      ].join(':')
    },
    datetimeRangeAsString(start, end) {
      if (!end || Math.abs(start.unix() - end.unix()) < 1) {
        // less than a second
        return start.format('LTS, D MMMM') // "11:27:45, 2 апреля"
      }
      if (start.clone().startOf('day').unix() === end.clone().startOf('day').unix()) {
        // same day '10:00:00 – 10:01:00, 2 апреля'
        return `${start.format('LTS')} – ${end.format('LTS, D MMMM')}`
      }
      // '11:35, 2 апреля – 2:36, 3 апреля'
      return `${start.format('LT, D MMMM')} – ${end.format('LT, D MMMM')}`
    },
    pointFormatter(point) {
      const eventId = point._eventId
      const event = this.$store.getters['event/event'](eventId)
      return this.ganttTooltip(event)
    },
    pointDescriptionFormatter(point) {
      const ix = point.index + 1
      const category = point.yCategory
      const from = new Date(point.x)
      const to = new Date(point.x2)
      return ix + '. ' + category + ', ' + from.toDateString() +
          ' to ' + to.toDateString() + '.'
    },
    onPointClick(clickEvent) {
      const eventId = clickEvent.point._eventId
      return this.openEventLink(eventId)
    },
    openEventLink(eventId, loadFromServer = true) {
      const { $store } = this
      const url = $store.getters['event/url'](eventId)
      if (url === null) {
        // pass
        // Сервер не знает асолютной ссылки на событие
      } else if (url !== undefined) {
        window.open(url, '_blank')
      } else if (loadFromServer) {
        $store.dispatch('event/get', { eventId })
          .then(() => this.openEventLink(eventId, false))
      } else {
        // eslint-disable-next-line no-console
        console.error(`cannot open link for event ${eventId}`)
      }
    },
    onSelection(selectionEvent, chart) {
      if (!selectionEvent.resetSelection) { // zoom in
        const { min: eventMin, max: eventMax } = selectionEvent.xAxis[0]
        this.ui.chartMinXAxisTs = eventMin
        this.ui.chartMaxXAxisTs = eventMax
        this.$emit('update:forceXAxisMin', eventMin)
        this.$emit('update:forceXAxisMax', eventMax)
      } else {
        // zoom-out
        const { dataMin, dataMax } = this.$refs.chart.chart.xAxis[0].getExtremes()
        this.ui.chartMinXAxisTs = moment(dataMin).startOf('day').valueOf()
        this.ui.chartMaxXAxisTs = moment(dataMax).endOf('day').valueOf()
        this.$emit('update:forceXAxisMin', null)
        this.$emit('update:forceXAxisMax', null)
      }
    },
    onRender(renderEvent, chart) {
      const { min, max } = chart.xAxis[0].getExtremes()
      this.ui.chartMinXAxisTs = this.ui.chartMinXAxisTs || min
      this.ui.chartMaxXAxisTs = this.ui.chartMaxXAxisTs || max
    },
  },
}
</script>

<style lang="sass" scoped>
  .chart
    width: 100%
    min-height: 200px
    padding-top: 16px
  .EventChart
    position: relative

    .chart-helper-block
      position: absolute
      top: -162px
      right: 10px
      z-index: 5
      max-width: 100%
</style>

<style lang="css">

.highcharts-figure, .highcharts-data-table table {
  min-width: 320px;
  max-width: 800px;
  margin: 1em auto;
}

.highcharts-data-table table {
  font-family: Verdana, sans-serif;
  border-collapse: collapse;
  border: 1px solid #EBEBEB;
  margin: 10px auto;
  text-align: center;
  width: 100%;
  max-width: 500px;
}
.highcharts-data-table caption {
    padding: 1em 0;
    font-size: 1.2em;
    color: #555;
}
.highcharts-data-table th {
  font-weight: 600;
  padding: 0.5em;
}
.highcharts-data-table td, .highcharts-data-table th, .highcharts-data-table caption {
    padding: 0.5em;
}
.highcharts-data-table thead tr, .highcharts-data-table tr:nth-child(even) {
    background: #f8f8f8;
}
.highcharts-data-table tr:hover {
    background: #f1f7ff;
}

.hide-zoom-out .highcharts-button.highcharts-reset-zoom {
  display: none;
}
</style>
